import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useAuth } from "context/AuthContext";
import { useStorefrontTenant } from "context/StorefrontTenantContext";

const CartContext = createContext(null);
const CART_STORAGE_PREFIX = "cart:";
const LEGACY_CART_STORAGE_KEY = "cart";

function safeParse(json, fallback) {
  try {
    const v = JSON.parse(json);
    return v ?? fallback;
  } catch {
    return fallback;
  }
}

function normalizeLine(line) {
  if (!line || typeof line !== "object") return null;
  const documentId = String(
    line.documentId || line.inventoryItemDocumentId || ""
  ).trim();
  if (!documentId) return null;
  const qty = Math.max(
    1,
    Math.floor(Number(line.qty || line.quantity || 1) || 1)
  );
  return {
    documentId,
    sku: line.sku ? String(line.sku) : "",
    name: line.name ? String(line.name) : "",
    unitPrice: line.unitPrice != null ? Number(line.unitPrice) : null,
    coreCharge: line.coreCharge != null ? Number(line.coreCharge) : null,
    imageUrl: line.imageUrl ? String(line.imageUrl) : null,
    qty,
  };
}

function normalizeCompanyKey(value) {
  return (
    String(value || "")
      .trim()
      .toLowerCase() || "unscoped"
  );
}

function cartStorageKey(companyKey) {
  return `${CART_STORAGE_PREFIX}${normalizeCompanyKey(companyKey)}`;
}

function readCartLines(storageKey) {
  const raw =
    typeof window !== "undefined" ? localStorage.getItem(storageKey) : null;
  const parsed = raw ? safeParse(raw, []) : [];
  return (Array.isArray(parsed) ? parsed : [])
    .map(normalizeLine)
    .filter(Boolean);
}

function clearStoredCarts() {
  if (typeof window === "undefined") return;
  try {
    Object.keys(localStorage).forEach((key) => {
      if (
        key === LEGACY_CART_STORAGE_KEY ||
        key.startsWith(CART_STORAGE_PREFIX)
      ) {
        localStorage.removeItem(key);
      }
    });
  } catch {
    // ignore storage errors
  }
}

export function CartProvider({ children }) {
  const { activeCompany } = useAuth();
  const { tenantKey } = useStorefrontTenant();
  const activeCartStorageKey = useMemo(
    () => cartStorageKey(tenantKey || activeCompany),
    [tenantKey, activeCompany]
  );
  const [cartState, setCartState] = useState(() => ({
    storageKey: activeCartStorageKey,
    lines: readCartLines(activeCartStorageKey),
  }));
  const lines = cartState.lines;

  useEffect(() => {
    setCartState((prev) =>
      prev.storageKey === activeCartStorageKey
        ? prev
        : {
            storageKey: activeCartStorageKey,
            lines: readCartLines(activeCartStorageKey),
          }
    );
  }, [activeCartStorageKey]);

  useEffect(() => {
    if (cartState.storageKey !== activeCartStorageKey) return;
    try {
      localStorage.removeItem(LEGACY_CART_STORAGE_KEY);
      localStorage.setItem(
        cartState.storageKey,
        JSON.stringify(cartState.lines)
      );
    } catch {
      // ignore storage errors
    }
  }, [activeCartStorageKey, cartState]);

  useEffect(() => {
    const clearCart = () => {
      clearStoredCarts();
      setCartState((prev) => ({ ...prev, lines: [] }));
    };
    window.addEventListener("cart-cleared", clearCart);
    return () => window.removeEventListener("cart-cleared", clearCart);
  }, []);

  const setLines = useCallback((updater) => {
    setCartState((prev) => ({
      ...prev,
      lines: typeof updater === "function" ? updater(prev.lines) : updater,
    }));
  }, []);

  const addItem = useCallback((item, qty = 1) => {
    const normalized = normalizeLine({ ...(item || {}), qty });
    if (!normalized) return;
    setLines((prev) => {
      const existingIdx = prev.findIndex(
        (l) => String(l.documentId) === String(normalized.documentId)
      );
      if (existingIdx === -1) return [...prev, normalized];
      const next = [...prev];
      next[existingIdx] = {
        ...next[existingIdx],
        qty: Math.max(1, next[existingIdx].qty + normalized.qty),
      };
      return next;
    });
  }, []);

  const removeItem = useCallback((documentId) => {
    const key = String(documentId || "");
    setLines((prev) => prev.filter((l) => String(l.documentId) !== key));
  }, []);

  const setQty = useCallback((documentId, qty) => {
    const key = String(documentId || "");
    const n = Math.max(1, Math.floor(Number(qty || 1) || 1));
    setLines((prev) =>
      prev.map((l) => (String(l.documentId) === key ? { ...l, qty: n } : l))
    );
  }, []);

  const clear = useCallback(() => setLines([]), []);

  const itemCount = useMemo(
    () => (lines || []).reduce((sum, l) => sum + (Number(l.qty) || 0), 0),
    [lines]
  );

  const subtotal = useMemo(() => {
    return (lines || []).reduce((sum, l) => {
      const unit = Number(l.unitPrice || 0);
      const core = Number(l.coreCharge || 0);
      const qty = Number(l.qty || 0);
      return sum + qty * (unit + core);
    }, 0);
  }, [lines]);

  const itemSubtotal = useMemo(
    () =>
      (lines || []).reduce(
        (sum, l) => sum + Number(l.qty || 0) * Number(l.unitPrice || 0),
        0
      ),
    [lines]
  );

  const coreSubtotal = useMemo(
    () =>
      (lines || []).reduce(
        (sum, l) => sum + Number(l.qty || 0) * Number(l.coreCharge || 0),
        0
      ),
    [lines]
  );

  const value = useMemo(
    () => ({
      lines,
      addItem,
      removeItem,
      setQty,
      clear,
      itemCount,
      subtotal,
      itemSubtotal,
      coreSubtotal,
    }),
    [
      lines,
      addItem,
      removeItem,
      setQty,
      clear,
      itemCount,
      subtotal,
      itemSubtotal,
      coreSubtotal,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

CartProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within a CartProvider");
  return ctx;
}
